import { computed } from 'vue';
import type { ComputedRef } from 'vue';
import { SmsAiDraftState } from '@/components/sms/aiDraft/types.ts';

export interface SmsAiDraftControlsStateProps {
    canGenerate: boolean;
    canUndo: boolean;
    canUpdate: boolean;
    disabled: boolean;
    hasGeneratedDraft: boolean;
    hasText: boolean;
    rateLimitSeconds: number;
    serviceLeadDisabled: boolean;
    state: SmsAiDraftState;
}

export interface UseSmsAiDraftControlsState {
    isInProgress: ComputedRef<boolean>;
    isFailed: ComputedRef<boolean>;
    isRateLimited: ComputedRef<boolean>;
    isNotEntitled: ComputedRef<boolean>;
    hasAlert: ComputedRef<boolean>;
    isGenerated: ComputedRef<boolean>;
    effectiveServiceLeadDisabled: ComputedRef<boolean>;
    canEditInstruction: ComputedRef<boolean>;
    canUseUndo: ComputedRef<boolean>;
    showAlert: ComputedRef<boolean>;
    showUpdatePanel: ComputedRef<boolean>;
    showUpdateServiceLeadHelper: ComputedRef<boolean>;
    generateDisabled: ComputedRef<boolean>;
    showGenerateButton: ComputedRef<boolean>;
    showGenerateServiceLeadHelper: ComputedRef<boolean>;
    showActionRow: ComputedRef<boolean>;
}

export const useSmsAiDraftControlsState = (
    props: SmsAiDraftControlsStateProps
): UseSmsAiDraftControlsState => {
    const isInProgress = computed<boolean>(() => props.state === SmsAiDraftState.Loading);
    const isFailed = computed<boolean>(() => props.state === SmsAiDraftState.Failed);
    const isRateLimited = computed<boolean>(() => props.state === SmsAiDraftState.RateLimited);
    const isNotEntitled = computed<boolean>(() => props.state === SmsAiDraftState.NotEntitled);
    const hasAlert = computed<boolean>(() => isFailed.value || isRateLimited.value);
    const isGenerated = computed<boolean>(() => {
        const stateHasGeneratedDraft =
            props.state === SmsAiDraftState.Inserted ||
            props.state === SmsAiDraftState.UpdateUnsupported;

        return props.hasGeneratedDraft && stateHasGeneratedDraft;
    });
    const effectiveServiceLeadDisabled = computed<boolean>(
        () => props.serviceLeadDisabled || props.state === SmsAiDraftState.ServiceLead
    );
    const canEditInstruction = computed<boolean>(
        () =>
            props.canUpdate &&
            !props.disabled &&
            !isInProgress.value &&
            !isRateLimited.value &&
            !effectiveServiceLeadDisabled.value
    );
    const canUseUndo = computed<boolean>(() => props.canUndo && !isInProgress.value);
    const showAlert = computed<boolean>(() => hasAlert.value);
    const showUpdatePanel = computed<boolean>(
        () =>
            !isNotEntitled.value &&
            props.hasText &&
            (props.canUpdate || effectiveServiceLeadDisabled.value)
    );
    const showUpdateServiceLeadHelper = computed<boolean>(
        () => effectiveServiceLeadDisabled.value && props.hasText && !isInProgress.value
    );
    const showGenerateServiceLeadHelper = computed<boolean>(
        () => effectiveServiceLeadDisabled.value && !props.hasText && !isInProgress.value
    );

    const generateDisabled = computed<boolean>(
        () =>
            props.disabled ||
            isInProgress.value ||
            isRateLimited.value ||
            effectiveServiceLeadDisabled.value ||
            !props.canGenerate
    );
    const showGenerateButton = computed<boolean>(
        () =>
            !props.hasText &&
            !isGenerated.value &&
            !isNotEntitled.value &&
            (props.canGenerate ||
                effectiveServiceLeadDisabled.value ||
                isInProgress.value ||
                hasAlert.value)
    );
    const showActionRow = computed<boolean>(
        () => showGenerateButton.value || showGenerateServiceLeadHelper.value
    );

    return {
        canEditInstruction,
        canUseUndo,
        effectiveServiceLeadDisabled,
        generateDisabled,
        hasAlert,
        isFailed,
        isGenerated,
        isInProgress,
        isNotEntitled,
        isRateLimited,
        showActionRow,
        showAlert,
        showGenerateButton,
        showGenerateServiceLeadHelper,
        showUpdatePanel,
        showUpdateServiceLeadHelper,
    };
};
